import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Spinner,
  Button,
  Accordion,
  Form,
} from "react-bootstrap";
import {
  FaCheckCircle,
  FaCarSide,
  FaStar,
  FaArrowRight,
  FaFileInvoice,
  FaArrowLeft,
  FaTruck,
  FaMapMarkerAlt,
  FaLocationArrow,
  FaFilePdf,
  FaMoneyBillWave,
} from "react-icons/fa";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { userAPI } from "../../api/apiSystem";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientChatBox from "../chat/ClientChatBox";
import { formatNumber } from "../../utils/helpers";
import "../clientTable/DarkClientStyles.css";

export default function BookingEmployeeDetails() {
  const { t } = useTranslation();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id, employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [userType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setLoading(true);
        const res = await userAPI.employees.showbooking(employeeId, id);
        if (res.success) {
          setBooking(res.data.data || res.data);
        }
      } catch (err) {
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };
    if (employeeId && id) fetchBooking();
  }, [id, employeeId]);

  const handleBack = () => {
    navigate(`/user/employees/${employeeId}/booking`, {
      state: {
        fromPage: location.state?.fromPage || 1,
        activeTab: location.state?.activeTab || "booking",
      },
    });
  };

  const handleViewTrack = (booking_id) => {
    navigate(
      `/user/employees/${employeeId}/booking/booking_details/track/${booking_id}`,
    );
  };

  const handleViewLiveTrack = (id) => {
    navigate(
      `/user/employees/${employeeId}/booking/booking_details/live-track/${id}`,
    );
  };

  // const handleViewTrack = () => {
  //   navigate(
  //     `/user/employees/${employeeId}/booking/booking_details/track/${id}`
  //   );
  // };

  const getArrayData = (data, index) => {
    if (Array.isArray(data)) return data[index] || "-";
    return data || "-";
  };

  const getExtraServices = () => {
    const services = booking?.extra_service;
    if (!services) return [];
    return Array.isArray(services) ? services : [services];
  };

  const getExtraServiceLabel = (service) =>
    ({ loading: t("loading_service"), unloading: t("unloading_service") }[service]) || t(service);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  if (!booking) {
    return (
      <Container className="py-5 text-center">
        <Card className="shadow-sm p-4">
          <h5 className="text-danger">
            🚫 {t("clientDetails.alerts.not_found.title")}
          </h5>
          <p className="text-muted">
            {t("clientDetails.alerts.not_found.message")}
          </p>
        </Card>
      </Container>
    );
  }

  const driver = booking.order || {};
  const rating = booking.rating || {};
  const orderStatus = booking?.order?.status?.toLowerCase();
  const showLiveTracking =
    Boolean(booking?.order) && !["pending", "completed"].includes(orderStatus);

  const loadingCount = Array.isArray(booking.booking_from) ? booking.booking_from.length : 0;
  const unloadingCount = Array.isArray(booking.booking_to) ? booking.booking_to.length : 0;
  const newLocal = "#ff7b00";

  return (
    <>
      <NavbarMenu />
      <div className="transport-details-page">
        <Container className="py-5">
          <div className="d-flex flex-wrap gap-2 mb-4 align-items-center">
            <Button variant="secondary" onClick={handleBack} size="sm">
              <FaArrowLeft className="me-1" />
              {t("clientDetails.back")}
            </Button>

            <Button
              variant="outline"
              style={{ color: newLocal, borderColor: newLocal }}
              size="sm"
              onClick={() => handleViewTrack(booking.booking_id)}
            >
              <FaTruck className="me-1" /> {t("clientDetails.track")}
            </Button>

            {showLiveTracking && (
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => handleViewLiveTrack(booking.order.id)}
              >
                <FaLocationArrow className="me-1" />
                {t("clientDetails.live_tracking")}
              </Button>
            )}
          </div>

          {/* Order number + status */}
          <Row className="align-items-center mb-3">
            <Col>
              <h5 className="fw-bold text-orange">
                {t("clientDetails.order")}: {booking.booking_id}
              </h5>
            </Col>
            <Col className="text-end">
              <Badge
                bg={
                  booking.status === "completed"
                    ? "success"
                    : booking.status === "pending"
                    ? "warning"
                    : "secondary"
                }
                className="px-3 py-2"
              >
                {t(`clientDetails.status.${booking.status}`) ||
                  t("clientDetails.status.unknown")}
              </Badge>
            </Col>
          </Row>

          {/* Progress */}
          <div className="progress-container">
            <div className="d-flex justify-content-between align-items-start">
              <div className="text-center">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle border border-2 mb-2 mx-auto"
                  style={{ width: 50, height: 50, backgroundColor: "rgba(239,237,237,1)" }}
                >
                  <FaCheckCircle
                    size={24}
                    className={
                      ["approved", "assigned", "received", "completed"].includes(
                        booking.status,
                      )
                        ? "text-success"
                        : "text-secondary"
                    }
                  />
                </div>
                <p className="mb-0 small">{t("clientDetails.progress.created")}</p>
              </div>
              <div
                className={`flex-grow-1 border-top mx-2 ${
                  ["received", "completed"].includes(booking.status)
                    ? "border-success"
                    : ""
                }`}
                style={{ marginTop: 24 }}
              ></div>
              <div className="text-center">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle border border-2 mb-2 mx-auto"
                  style={{ width: 50, height: 50, backgroundColor: "rgba(217,217,217,0.51)" }}
                >
                  <FaCarSide
                    size={24}
                    className={
                      ["received", "completed"].includes(booking.status)
                        ? "text-success"
                        : "text-secondary"
                    }
                  />
                </div>
                <p className="mb-0 small">{t("clientDetails.progress.on_the_way")}</p>
              </div>
              <div
                className={`flex-grow-1 border-top mx-2 ${
                  booking.status === "completed" ? "border-success" : ""
                }`}
                style={{ marginTop: 24 }}
              ></div>
              <div className="text-center">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle border border-2 mb-2 mx-auto"
                  style={{ width: 50, height: 50, backgroundColor: "rgba(239,237,237,1)" }}
                >
                  <FaCheckCircle
                    size={24}
                    className={
                      booking.status === "completed" ? "text-success" : "text-secondary"
                    }
                  />
                </div>
                <p className="mb-0 small">{t("clientDetails.progress.completed")}</p>
              </div>
            </div>
          </div>

          {/* Locations */}
          <Row className="mb-4">
            <Col md={6}>
              <h6 className="fw-bold text-orange mb-3">
                {t("clientDetails.pickup.title")} ({loadingCount}{" "}
                {t("clientDetails.pickup.locations")})
              </h6>
              <Accordion defaultActiveKey={Array.from({ length: loadingCount }, (_, i) => i.toString())}>
                {Array.from({ length: loadingCount }).map((_, index) => (
                  <Accordion.Item key={index} eventKey={index.toString()}>
                    <Accordion.Header>
                      <FaMapMarkerAlt className="me-2 text-primary" />
                      {t("clientDetails.pickup.location")} {index + 1}:{" "}
                      {getArrayData(booking.booking_from, index)}
                    </Accordion.Header>
                    <Accordion.Body>
                      <Card className="border-0 p-3">
                        <p><strong>{t("clientDetails.pickup.company")}:</strong> {getArrayData(booking.loading_company, index)}</p>
                        <p><strong>{t("clientDetails.pickup.name")}:</strong> {getArrayData(booking.loading_first_name, index)} {getArrayData(booking.loading_last_name, index)}</p>
                        <p><strong>{t("clientDetails.pickup.address")}:</strong> {getArrayData(booking.loading_streat_no, index)}</p>
                        <p><strong>{t("clientDetails.pickup.postal_code")}:</strong> {getArrayData(booking.loading_postal, index)}</p>
                        <p><strong>{t("clientDetails.pickup.phone")}:</strong> {getArrayData(booking.loading_phone, index)}</p>
                        <p><strong>{t("clientDetails.pickup.date_time")}:</strong> {getArrayData(booking.loading_date_time, index)}</p>
                      </Card>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
            <Col md={6}>
              <h6 className="fw-bold text-orange mb-3">
                {t("clientDetails.delivery.title")} ({unloadingCount}{" "}
                {t("clientDetails.delivery.locations")})
              </h6>
              <Accordion defaultActiveKey={Array.from({ length: unloadingCount }, (_, i) => i.toString())}>
                {Array.from({ length: unloadingCount }).map((_, index) => (
                  <Accordion.Item key={index} eventKey={index.toString()}>
                    <Accordion.Header>
                      <FaMapMarkerAlt className="me-2 text-success" />
                      {t("clientDetails.delivery.location")} {index + 1}:{" "}
                      {getArrayData(booking.booking_to, index)}
                    </Accordion.Header>
                    <Accordion.Body>
                      <Card className="border-0 p-3">
                        <p><strong>{t("clientDetails.delivery.company")}:</strong> {getArrayData(booking.unloading_company, index)}</p>
                        <p><strong>{t("clientDetails.delivery.name")}:</strong> {getArrayData(booking.unloading_first_name, index)} {getArrayData(booking.unloading_last_name, index)}</p>
                        <p><strong>{t("clientDetails.delivery.address")}:</strong> {getArrayData(booking.unloading_streat_no, index)}</p>
                        <p><strong>{t("clientDetails.delivery.postal_code")}:</strong> {getArrayData(booking.unloading_postal, index)}</p>
                        <p><strong>{t("clientDetails.delivery.phone")}:</strong> {getArrayData(booking.unloading_phone, index)}</p>
                        <p><strong>{t("clientDetails.delivery.date_time")}:</strong> {getArrayData(booking.unloading_date_time, index)}</p>
                      </Card>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>

          {/* Shipment */}
          <h6 className="fw-bold text-orange mb-2">{t("clientDetails.shipment.title")}</h6>
          <Card className="border-0 mb-4" style={{ background: "rgba(239,237,237,1)" }}>
            <Table bordered responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>{t("clientDetails.shipment.article")}</th>
                  <th>{t("clientDetails.shipment.weight")}</th>
                  <th>{t("clientDetails.shipment.dimensions")}</th>
                  <th>{t("clientDetails.shipment.quantity")}</th>
                </tr>
              </thead>
              <tbody>
                {booking.shipment_data?.map((item, i) => (
                  <tr key={i}>
                    <td>{t(item.shipment_data)}</td>
                    {item.shipment_data !== "dokument" ? (
                      <>
                        <td>{formatNumber(item.weight)} kg</td>
                        <td>{item.length}x{item.width}x{item.height} cm</td>
                      </>
                    ) : (
                      <><td>-</td><td>-</td></>
                    )}
                    <td>{item.quantity || 1}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} className="fw-bold">{t("clientDetails.shipment.total_weight")}:</td>
                  <td className="fw-bold text-orange">{formatNumber(booking.total_weight) || "-"} Kg</td>
                </tr>
                <tr>
                  <td colSpan={3} className="fw-bold">{t("clientDetails.shipment.distance")}:</td>
                  <td className="fw-bold">{booking.distance || "-"}</td>
                </tr>
                <tr>
                  <td colSpan={3} className="fw-bold">{t("clientDetails.shipment.duration")}:</td>
                  <td className="fw-bold">{booking.total_duration || "-"}</td>
                </tr>
              </tbody>
            </Table>
          </Card>

          {/* Vehicle */}
          <h6 className="fw-bold text-orange mb-2">{t("clientDetails.vehicle.title")}</h6>
          <Card className="border-0 p-3 mb-4">
            <p className="fw-semibold mb-1">
              {booking.vehicle_name}
              {Number(booking.vehicle_id) === 4 &&
                ` (${booking.crane === "yes" ? t("driverCard.general.withCrane") : t("driverCard.general.withoutCrane")})`}
            </p>
            <p className="text-muted small mb-1">{booking.vehicle_size}</p>
          </Card>

          {/* Additional info + Driver */}
          <Row>
            <Col md={8}>
              <Card className="border-0 p-3 mb-4">
                <h6 className="fw-bold text-orange mb-2">
                  ℹ️ {t("clientDetails.additional_info.title")}
                </h6>
                <p className="text-muted">{booking.user_note || "-"}</p>
                <div className="mt-3">
                  <strong>{t("extra_services")}:</strong>
                  {getExtraServices().length > 0 ? (
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {getExtraServices().map((service) => (
                        <Badge key={service} bg="warning" text="dark">
                          {getExtraServiceLabel(service)}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted ms-2">{t("no_extra_services")}</span>
                  )}
                </div>
                <hr />
                <Row className="mt-3 align-items-start text-center">
                  <Col>
                    <p className="mb-0">
                      {t("clientDetails.additional_info.pickup_time")}:{" "}
                      <strong>{getArrayData(booking.loading_date_time, 0) || "-"}</strong>
                    </p>
                  </Col>
                  <Col xs="auto"><FaArrowRight className="text-orange" size={20} /></Col>
                  <Col>
                    <p className="mb-0">
                      {t("clientDetails.additional_info.delivery_time")}:{" "}
                      <strong>{getArrayData(booking.unloading_date_time, 0) || "-"}</strong>
                    </p>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 h-100 p-3">
                <h6 className="fw-bold text-center text-warning mb-3">
                  👨‍✈️ {t("clientDetails.driver.title")}
                </h6>

                {booking.status === "completed" && (
                  <div className="text-center mb-3">
                    {!rating.value || rating.value === 0 ? null : (
                      [...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          size={18}
                          className={`me-1 ${i < rating.value ? "text-warning" : "text-muted"}`}
                        />
                      ))
                    )}
                    {rating.comment && (
                      <p className="small text-muted fst-italic mb-0">
                        &quot;{rating.comment}&quot;
                      </p>
                    )}
                  </div>
                )}

                {booking.order ? (
                  <>
                    <div className="text-center">
                      <h6 className="mb-0">
                        {driver.driver_name || t("clientDetails.driver.unknown")}
                      </h6>
                      <p className="text-muted small mb-2">{driver.phone_number || "-"}</p>
                    </div>
                    <ul className="list-unstyled small mb-3 text-center mt-2">
                      <li>{t("clientDetails.driver.vehicle")}: {driver.vehicle_type || "-"}</li>
                      <li>{t("clientDetails.driver.number")}: {driver.vehicle_number || "-"}</li>
                      <li>{t("clientDetails.driver.location")}: {driver.current_location || "-"}</li>
                      <li>{t("clientDetails.driver.status")}: {driver.status || "N/A"}</li>
                    </ul>
                  </>
                ) : (
                  <p className="text-muted text-center">
                    {t("clientDetails.driver.no_driver_assigned")}
                  </p>
                )}
              </Card>
            </Col>
          </Row>

          {/* Invoice */}
          <h6 className="fw-bold text-orange">
            <FaFileInvoice className="me-2" /> {t("clientDetails.invoice.title")}
          </h6>
          <Card className="border-0 mb-4 p-3">
            <Row>
              <Col md={6}>
                <p><strong>{t("clientDetails.invoice.title_field")}:</strong> {booking.invoice_title}</p>
                <p><strong>{t("clientDetails.invoice.first_name")}:</strong> {booking.invoice_first_name}</p>
                <p><strong>{t("clientDetails.invoice.last_name")}:</strong> {booking.invoice_last_name}</p>
                <p><strong>{t("clientDetails.invoice.company")}:</strong> {booking.invoice_company}</p>
                <p><strong>{t("clientDetails.invoice.email")}:</strong> {booking.invoice_email}</p>
                <p><strong>{t("clientDetails.invoice.email_2")}:</strong> {booking.invoice_email_2}</p>
              </Col>
              <Col md={6}>
                <p><strong>{t("clientDetails.invoice.street")}:</strong> {booking.invoice_street_no}</p>
                <p><strong>{t("clientDetails.invoice.additional_address")}:</strong> {booking.invoice_addition_address}</p>
                <p><strong>{t("clientDetails.invoice.postal_city")}:</strong> {booking.invoice_postal_code}</p>
                <p><strong>{t("clientDetails.invoice.phone")}:</strong> {booking.invoice_phone_number}</p>
                <p><strong>{t("clientDetails.invoice.reference")}:</strong> {booking.invoice_reffernce}</p>
              </Col>
            </Row>
          </Card>

          {/* Documents */}
          <h6 className="fw-bold text-orange">
            <FaFilePdf className="me-2" /> {t("clientDetails.documents.title")}
          </h6>
          <Card className="border-0 mb-5 p-3 text-center">
            {booking.invoice_path ? (
              <div className="d-flex gap-2 justify-content-center flex-wrap">
                {booking.show_invoice && (
                  <Button className="btn-light" size="sm" onClick={() => window.open(booking.invoice_path, "_blank")}>
                    <FaFileInvoice className="me-2" />
                    {t("clientDetails.documents.view_invoice")}
                  </Button>
                )}
                {booking.bill_path && booking.bill_path.map((billUrl, index) => (
                  <Button key={index} className="btn-light" size="sm" onClick={() => window.open(billUrl, "_blank")}>
                    <FaFileInvoice className="me-2" /> CMR
                  </Button>
                ))}
              </div>
            ) : (
              <p className="text-muted mb-0">{t("clientDetails.documents.no_documents")}</p>
            )}
          </Card>

          {/* Payment */}
          <h6 className="fw-bold text-orange mt-4">
            <FaMoneyBillWave className="me-2" /> {t("clientDetails.payment.title")}
          </h6>
          <Card className="border-0 mb-4 p-3">
            <Row>
              <p className="text-center">
                <strong>{booking.sub_total} € {t("clientDetails.payment.netto")}</strong> <br />
                {t("clientDetails.payment.plus_vat")}{" "}
                <strong>{booking.total} € {t("clientDetails.payment.brutto")}</strong>
              </p>
            </Row>
            <Row>
              <Col md={12}>
                <p>
                  <strong>{t("clientDetails.payment.status")}:</strong>{" "}
                  <Badge bg={booking.payment_status === 1 ? "success" : "secondary"}>
                    {booking.payment || t("clientDetails.payment.unpaid")}
                  </Badge>
                </p>
              </Col>
              <Col md={6}>
                <p><strong>{t("clientDetails.payment.method")}:</strong> {booking.payment_method || "-"}</p>
              </Col>
            </Row>
          </Card>
        </Container>
      </div>

      <Footer />

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}